import { createFileRoute, redirect } from "@tanstack/react-router";
import queryClient from "api/queryClient.ts";
import tenantAxiosInstance from "api/tenantAxiosInstance";
import { cascadeProcessApi } from "api/TenantSlug/CascadeProcess/api.ts";
import { Cascade } from "components/UI/Cascade/Cascade.tsx";
import i18next from "i18next";
import { isAxiosErrorOfStatus } from "utils/isAxiosErrorOfStatus.ts";

import GetCascadeProcessExternalFinalPageSettingResponse = App.Http.Responses.Cascade.GetCascadeProcessExternalFinalPageSettingResponse;
import GetConversionApiExternalFinalPageResponse = App.Http.Responses.Conversion.GetConversionApiExternalFinalPageResponse;

const EXTERNAL_FINAL_PAGE_STATUS_CODE = 202;
const SUPPORTED_LANGUAGES = ["cs", "en"] as const;
// Matches the BE's own fallback (ProcessLanguageResolver) — only used if the API response omits
// `language` entirely, since BE always backfills a stored/country-default value otherwise.
const DEFAULT_LANGUAGE = "en";

type FinalPageLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const isSupportedLanguage = (lang: unknown): lang is FinalPageLanguage =>
    SUPPORTED_LANGUAGES.includes(lang as FinalPageLanguage);

export const Route = createFileRoute("/$tenantSlug/processes/_layout/$processId/")({
    validateSearch: (search: Record<string, unknown>): { lang?: FinalPageLanguage } => ({
        lang: isSupportedLanguage(search.lang) ? search.lang : undefined,
    }),
    loaderDeps: ({ search }) => ({ lang: search.lang }),
    loader: async ({ params, deps }) => {
        const { tenantSlug, processId } = params;
        tenantAxiosInstance.setTenantSlugInUrl(tenantSlug);

        const [cascadeProcess, setting] = await Promise.all([
            queryClient.fetchQuery({
                queryKey: ["getCascadeProcess", tenantSlug, processId],
                queryFn: () => cascadeProcessApi.show(processId),
            }),
            queryClient.fetchQuery({
                queryKey: ["getCascadeProcessExternalFinalPageSetting", tenantSlug, processId],
                queryFn: () => fetchExternalFinalPageSetting(processId),
            }),
        ]);

        // Priority: ?lang= query param > language resolved by the API (explicit lang / offer country) > default
        const processLanguage = (cascadeProcess.data as { language?: string }).language;
        await i18next.changeLanguage(
            deps.lang ?? (isSupportedLanguage(processLanguage) ? processLanguage : DEFAULT_LANGUAGE)
        );

        if (cascadeProcess.status !== EXTERNAL_FINAL_PAGE_STATUS_CODE) {
            return;
        }

        const cascade = cascadeProcess.data as GetConversionApiExternalFinalPageResponse;

        if (setting?.skip_internal_loader && cascade.url) {
            throw redirect({ href: cascade.url });
        }
    },
    component: ProcessPage,
});

async function fetchExternalFinalPageSetting(
    processId: string
): Promise<GetCascadeProcessExternalFinalPageSettingResponse | undefined> {
    try {
        return await cascadeProcessApi.showExternalFinalPageSetting(processId);
    } catch (error) {
        if (isAxiosErrorOfStatus(error, 404)) {
            return undefined;
        }

        throw error;
    }
}

function ProcessPage() {
    const params = Route.useParams();

    return <Cascade processId={params.processId} />;
}
